import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import CollaborationIcon from "./CollaborationIcon";
import { formatJourneyStageMeta } from "../collaborationUtils";

const JourneyStepper = ({ journey }) => {
  if (!journey?.stages?.length) {
    return null;
  }

  return (
    <JourneyHeader>
      <JourneyLabel>
        <SectionLabel>Unsere Reise</SectionLabel>
      </JourneyLabel>
      <Stepper>
        {journey.stages.map(stage => (
          <Step key={stage.id} $status={stage.status}>
            <StepMarker $status={stage.status}>
              {stage.status === "done" && (
                <CollaborationIcon name="check" size={14} />
              )}
              {stage.status === "active" && <ActiveDot />}
            </StepMarker>
            <StepText>
              <StepName $status={stage.status}>{stage.label}</StepName>
              <StepMeta $status={stage.status}>
                {formatJourneyStageMeta(stage)}
              </StepMeta>
            </StepText>
          </Step>
        ))}
      </Stepper>
    </JourneyHeader>
  );
};

JourneyStepper.propTypes = {
  journey: PropTypes.shape({
    current: PropTypes.string,
    stages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        date: PropTypes.string,
        meta: PropTypes.string
      })
    )
  })
};

export default JourneyStepper;

const JourneyHeader = styled.section`
  background: #f7f6f2;
  padding: 22px 32px 20px;
  border-bottom: 1px solid #ebe9e2;
  flex-shrink: 0;
`;

const JourneyLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a09e96;
`;

const Dot = styled.span`
  color: #c8c5bc;
`;

const Current = styled.span`
  color: #0a7c4e;
`;

const Stepper = styled.div`
  display: flex;
  position: relative;
`;

const Step = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  gap: 8px;

  &::after {
    content: "";
    position: absolute;
    top: 11px;
    left: calc(50% + 14px);
    right: calc(-50% + 14px);
    height: 2px;
    background: ${({ $status }) =>
    $status === "done" ? "#0a7c4e" : $status === "tbd" ? "transparent" : "#d8d4c8"};
    border-top: ${({ $status }) =>
    $status === "tbd" ? "2px dashed #d8d4c8" : "none"};
    z-index: 0;
  }

  &:last-child::after {
    display: none;
  }
`;

const SectionLabel = styled.h2`
  margin: ${({ $spaced }) => ($spaced ? "40px 0 12px" : "0 0 12px")};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a09e96;
`;

const StepMarker = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  flex-shrink: 0;
  background: ${({ $status }) => {
    if ($status === "done" || $status === "active") return "#0a7c4e";
    if ($status === "tbd") return "transparent";
    return "#f0eee8";
  }};
  color: ${({ $status }) =>
    $status === "done" || $status === "active" ? "#fff" : "#a09e96"};
  border: 2px solid
    ${({ $status }) => ($status === "tbd" ? "#d8d4c8" : "#f7f6f2")};
  border-style: ${({ $status }) => ($status === "tbd" ? "dashed" : "solid")};
  box-shadow: ${({ $status }) =>
    $status === "active" ? "0 0 0 4px rgba(10, 124, 78, 0.15)" : "none"};
`;

const ActiveDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
`;

const StepText = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const StepName = styled.div`
  font-size: 11px;
  font-weight: 600;
  white-space: pre-line;
  text-transform: ${({ $status }) => ($status === "tbd" ? "none" : "uppercase")};
  letter-spacing: ${({ $status }) => ($status === "tbd" ? "0" : "0.04em")};
  font-style: ${({ $status }) => ($status === "tbd" ? "italic" : "normal")};
  color: ${({ $status }) => {
    if ($status === "done") return "#181714";
    if ($status === "active") return "#0a7c4e";
    if ($status === "tbd") return "#c8c5bc";
    return "#6a6960";
  }};
`;

const StepMeta = styled.div`
  font-size: 10px;
  font-weight: ${({ $status }) => ($status === "active" ? 600 : 500)};
  color: ${({ $status }) => ($status === "active" ? "#0a7c4e" : "#a09e96")};
`;
